<script setup lang="ts">
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import axios from 'axios'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import ScheduleStats from '@/Components/Student/Schedule/ScheduleStats.vue'
import SessionCalendar from '@/Components/Schedule/SessionCalendar.vue'
import MyTutorsExtended from '@/Components/Student/Schedule/MyTutorsExtended.vue'
import CalendarSessionModal from '@/Components/Schedule/CalendarSessionModal.vue'
import CalendarSyncModal from '@/Components/Schedule/CalendarSyncModal.vue'

interface ScheduleStatsData {
  sessionsThisMonth: number
  oneOnOneSessions: number
  groupSessions: number
  totalHours: number
  nextSession: {
    date: string
    time: string | null
    subject: string
    type: string
  } | null
}

interface Session {
  id: number
  schedule_id?: number
  title: string
  date: string
  time: string
  raw_time?: string
  type: 'one-on-one' | 'group'
  status: 'completed' | 'ongoing' | 'upcoming' | 'fixed'
  subject_name?: string
  level_name?: string
  note?: string | null
  topic?: string | null
  duration_minutes?: number
  is_projected?: boolean
  lesson_links?: {
    room_id: string
    teacher_link: string
    student_link: string
    generated_at: string
  } | null
}

interface Tutor {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  subject: string
  sessions_count: number
  rating: number
  type: 'one-on-one' | 'group'
  schedule_days: string[]
}

const props = defineProps<{
  stats: ScheduleStatsData
  sessions: Session[]
  tutors: Tutor[]
  currentMonth: string
}>()

const allSessions = ref<Session[]>([...props.sessions])
const fetchedMonths = ref(new Set<string>())
const isLoadingMonth = ref(false)
const liveStats = ref<ScheduleStatsData>({ ...props.stats })

// Mark initially loaded months as fetched
props.sessions.forEach(s => {
  if (s.date) fetchedMonths.value.add(s.date.substring(0, 7))
})

const showSessionModal = ref(false)
const selectedSession = ref<Session | null>(null)
const showSyncModal = ref(false)

function handleSessionClick(session: Session) {
  selectedSession.value = session
  showSessionModal.value = true
}

function handleStatsChange(stats: ScheduleStatsData) {
  liveStats.value = stats
}

async function handleMonthChange({ monthStr }: { year: number, month: number, monthStr: string }) {
  if (fetchedMonths.value.has(monthStr)) return

  isLoadingMonth.value = true
  try {
    const response = await axios.get(`/student/schedule/sessions`, {
      params: { month: monthStr },
    })
    const newSessions: Session[] = response.data.sessions || []
    const existingKeys = new Set(allSessions.value.map(s => `${s.id}:${s.date}`))
    newSessions.forEach(s => {
      const key = `${s.id}:${s.date}`
      if (!existingKeys.has(key)) {
        allSessions.value.push(s)
        existingKeys.add(key)
      }
    })
    fetchedMonths.value.add(monthStr)
  } catch (err) {
    console.error('Failed to fetch sessions for month:', monthStr, err)
  } finally {
    isLoadingMonth.value = false
  }
}
</script>

<template>
  <Head title="Calendar" />

  <DashboardLayout>
    <div class="space-y-8">
      <!-- Schedule Stats -->
      <ScheduleStats :stats="liveStats" />

      <!-- Calendar -->
      <SessionCalendar
        :sessions="allSessions"
        :current-month="currentMonth"
        header-gradient="from-teal-500 to-teal-800"
        :show-enrollments="false"
        :is-loading="isLoadingMonth"
        legend-hint="Click on a session to view details"
        @session-click="handleSessionClick"
        @month-change="handleMonthChange"
        @stats-change="handleStatsChange"
        @sync-calendar="showSyncModal = true"
      />

      <!-- My Tutors -->
      <MyTutorsExtended :tutors="tutors" />
    </div>

    <!-- Session Detail Modal (view only for students) -->
    <CalendarSessionModal
      :show="showSessionModal"
      :session="selectedSession"
      :can-edit-note="false"
      @close="showSessionModal = false"
    />
    <CalendarSyncModal
      :show="showSyncModal"
      @close="showSyncModal = false"
    />
  </DashboardLayout>
</template>
